/**
 * Shared Zustand form-state kit for the feature stores. Collapses the
 * applyUpdater + setter + handleChange + CRUD-formHandler boilerplate the stores
 * hand-rolled. Typed generically over each store's state interface.
 */
import { queryClient } from "../lib/queryClient";
import { validateMe } from "../utils/validation";
import { apiId } from "../utils/identity";
import { notify } from "../services/notify";
import { MSG } from "../services/messages";
import { getErrorMessage, getFieldErrors } from "../services/response";
import type { StrapiFieldError } from "../services/response";
import type { Identifiable } from "../types/entities";
import type { EntityPayload } from "../types/api";
import type { OpenMode } from "../types/ui";

/** A value, or a function of the previous value (React-setState style). */
export type Updater<T> = T | ((prev: T) => T);

/**
 * The value types a form field-change handler can carry: text (string), numeric
 * inputs, checkbox/switch booleans, a date-picker Date, or a cleared value
 * (null). Widened from the former string-only param so the checkbox/switch/date
 * handlers type-check without a cast.
 */
export type FieldValue = string | number | boolean | Date | null;

/** The subset of Zustand's `set`/`get` these helpers use. */
type SetState<S> = (partial: Partial<S> | ((s: S) => Partial<S>)) => void;
type GetState<S> = () => S;

/** Apply either a functional updater or a plain value against the previous state. */
// NOTE: `function` (not a `<T>(...) =>` arrow) — vite runs .ts through the JSX/TSX
// loader, which parses a bare generic arrow as a JSX tag. Same for capitalize below.
export function applyUpdater<T>(updater: Updater<T>, prev: T): T {
  return typeof updater === "function" ? (updater as (p: T) => T)(prev) : updater;
}

function capitalize<S extends string>(s: S): Capitalize<S> {
  return (s.charAt(0).toUpperCase() + s.slice(1)) as Capitalize<S>;
}

/** The `setX` map produced by makeSetters for a list of state keys. */
type Setters<S, K extends keyof S & string> = {
  [P in K as `set${Capitalize<P>}`]: (u: Updater<S[P]>) => void;
};

/**
 * Build the `setX: (u) => set(s => ({ x: applyUpdater(u, s.x) }))` setters for a
 * list of state keys: `makeSetters(set, ["open", "query"])` → { setOpen, setQuery }.
 * Spread the result into the store; keep any non-trivial setter written by hand.
 */
export function makeSetters<S, K extends keyof S & string>(
  set: SetState<S>,
  keys: readonly K[]
): Setters<S, K> {
  const out: Record<string, (u: Updater<S[K]>) => void> = {};
  for (const key of keys) {
    out[`set${capitalize(key)}`] = (u) =>
      set((s) => ({ [key]: applyUpdater(u, s[key]) }) as unknown as Partial<S>);
  }
  return out as Setters<S, K>;
}

/**
 * The `handleChange(value, name, type)` field writer shared by the CRUD form
 * stores: write inputs[name]=value, validate via validateMe(type, value), record
 * errorHandling[name]. `derive(value, name)` (optional) returns extra input
 * patches merged alongside — the room store uses it to set bundesland_id.
 */
export function makeFieldChangeHandler<S>(
  set: SetState<S>,
  opts: {
    inputsKey: keyof S & string;
    errorsKey?: keyof S & string;
    derive?: (value: string, name: string) => Record<string, unknown>;
  }
) {
  const { inputsKey, errorsKey = "errorHandling" as keyof S & string, derive } = opts;
  // Fields hold more than text: checkboxes write booleans and relation pickers
  // write a numeric id or null, so the value is not string-only. `derive` +
  // `validateMe` only ever act on the (string) text fields, so non-string values
  // are coerced away for them — a no-op for the fields they actually target.
  return (value: FieldValue, name: string, type?: string): void => {
    set(
      (s) =>
        ({
          [inputsKey]: {
            ...(s[inputsKey] as Record<string, unknown>),
            [name]: value,
            ...(derive ? derive(typeof value === "string" ? value : "", name) : {}),
          },
        }) as unknown as Partial<S>
    );
    const valid = validateMe(type, typeof value === "string" ? value : null);
    set(
      (s) =>
        ({
          [errorsKey]: { ...(s[errorsKey] as Record<string, unknown>), [name]: valid },
        }) as unknown as Partial<S>
    );
  };
}

/** The minimal state shape makeCrudFormHandler resets on success/error. */
interface CrudFormState {
  open: OpenMode;
  loading: boolean;
  formErrorHandling: StrapiFieldError[] | null;
  errorHandling: Record<string, unknown>;
}

interface CrudApi {
  update: (id: string, data: EntityPayload) => Promise<{ data?: unknown } | undefined>;
  create: (data: EntityPayload) => Promise<{ data?: unknown } | undefined>;
}

/**
 * The create/edit/copy save envelope shared by referent/room: preventDefault →
 * loading → serialize inputs → update(apiId) on edit, else create → on success
 * notify + reset + invalidate; on error set field errors + toast.
 */
export function makeCrudFormHandler<S extends CrudFormState, I extends Identifiable>(
  set: SetState<S>,
  get: GetState<S>,
  opts: {
    api: CrudApi;
    serializer: (inputs: I) => EntityPayload;
    queryKey: readonly unknown[];
    label: string;
    inputsKey: keyof S & string;
    onSuccess?: (result: { data?: unknown }) => void;
  }
) {
  const { api, serializer, queryKey, label, inputsKey, onSuccess } = opts;
  return async (event: { preventDefault: () => void }, open: string): Promise<void> => {
    event.preventDefault();
    set({ loading: true } as Partial<S>);
    try {
      const inputs = get()[inputsKey] as I;
      const data = serializer(inputs);

      let result: { data?: unknown } | undefined;
      if (open === "edit") {
        result = await api.update(apiId(inputs), data);
      }
      if (open === "add" || open === "copy") {
        result = await api.create(data);
      }

      if (result?.data) {
        notify("info", MSG.entity.saved(label, open === "edit"));
        set({
          open: false,
          loading: false,
          [inputsKey]: {},
          formErrorHandling: null,
          errorHandling: {},
        } as unknown as Partial<S>);
        queryClient.invalidateQueries({ queryKey: queryKey as unknown[] });
        onSuccess?.(result);
      } else {
        // A 2xx with no body (or an unrecognized `open`) — clear the spinner
        // instead of leaving the modal's save button stuck.
        set({ loading: false } as Partial<S>);
      }
    } catch (err) {
      set({ formErrorHandling: getFieldErrors(err), loading: false } as Partial<S>);
      notify("error", getErrorMessage(err));
    }
  };
}
